import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { Vitals } from '../pages/PDirectory/Intake/VitalForm'
import { CustomOptionState, graphState, MedicationStore, PatientData, RoleData } from './store.types'

export const useGraphStateStore = create<graphState>(set => ({
  isGraphOpen: false,
  setGraphState: submitted => set({ isGraphOpen: submitted })
}))

export const useCustomOptionStore = create<CustomOptionState>(set => ({
  isCustomFrequencySelected: false,
  setIsCustomFrequencySelected: selected => set({ isCustomFrequencySelected: selected })
}))

type ClinicalNoteLeaveAction = () => void

type ClinicalNoteLeaveGuardStore = {
  confirmLeave: (onLeave: ClinicalNoteLeaveAction) => boolean
  registerGuard: (handler: ((onLeave: ClinicalNoteLeaveAction) => boolean) | null) => void
}

export const useClinicalNoteLeaveGuardStore = create<ClinicalNoteLeaveGuardStore>(set => ({
  confirmLeave: onLeave => {
    onLeave()
    return true
  },
  registerGuard: handler =>
    set({
      confirmLeave:
        handler ??
        (onLeave => {
          onLeave()
          return true
        })
    })
}))

export const requestClinicalNoteLeave = (onLeave: ClinicalNoteLeaveAction) => {
  return useClinicalNoteLeaveGuardStore.getState().confirmLeave(onLeave)
}

const initialData = {
  category: '',
  title: ''
}

export const usePatientData = create<PatientData>()(
  persist(
    set => ({
      data: { name: '', ref: '' },
      reset: () =>
        set({
          data: { name: '', ref: '' }
        }),
      setData: value => set({ data: value })
    }),
    { name: 'document_template', storage: createJSONStorage(() => sessionStorage) }
  )
)

export interface CompareVitalsState {
  selectedDates: string[]
  setSelectedDates: (dates: string[]) => void
  comparedVitals: Record<string, any[]> // key: captured_date, value: array of vitals
  setComparedVitals: (data: Record<string, any[]>) => void
  resetCompare: () => void
}

export const useCompareVitalsStore = create<CompareVitalsState>(set => ({
  comparedVitals: {},
  resetCompare: () => set({ comparedVitals: {}, selectedDates: [] }),
  selectedDates: [],
  setComparedVitals: data => set({ comparedVitals: data }),
  setSelectedDates: dates => set({ selectedDates: dates })
}))

interface CompareVitalsFilterState {
  selectedVitalTypes: string[]
  setSelectedVitalTypes: (types: string[]) => void
  selectedDateRange: string
  setSelectedDateRange: (range: string) => void
}

export const useCompareVitalsFilterStore = create<CompareVitalsFilterState>()(
  persist(
    set => ({
      selectedDateRange: '',
      selectedVitalTypes: [],
      setSelectedDateRange: (range: string) => set({ selectedDateRange: range }),
      setSelectedVitalTypes: (types: string[]) => set({ selectedVitalTypes: types })
    }),
    { name: 'compare_vitals_filters', storage: createJSONStorage(() => localStorage) }
  )
)

interface CompareLabFilterState {
  selectedSubcategories: string[]
  setSelectedSubcategories: (types: string[]) => void
  selectedDateRange: string
  setSelectedDateRange: (range: string) => void
}

export const useCompareLabFilterStore = create<CompareLabFilterState>()(
  persist(
    set => ({
      selectedDateRange: '',
      selectedSubcategories: [],
      setSelectedDateRange: (range: string) => set({ selectedDateRange: range }),
      setSelectedSubcategories: (types: string[]) => set({ selectedSubcategories: types })
    }),
    { name: 'compare_lab_filters', storage: createJSONStorage(() => localStorage) }
  )
)

interface CompareImagingFilterState {
  selectedSubcategories: string[]
  setSelectedSubcategories: (types: string[]) => void
  selectedDateRange: string
  setSelectedDateRange: (range: string) => void
}

interface ImagePreviewState {
  imgFile: File | null
  setImgFile: (file: File | null) => void
}

export const useCompareImagingFilterStore = create<CompareImagingFilterState>()(
  persist(
    set => ({
      selectedDateRange: '',
      selectedSubcategories: [],
      setSelectedDateRange: (range: string) => set({ selectedDateRange: range }),
      setSelectedSubcategories: (types: string[]) => set({ selectedSubcategories: types })
    }),
    { name: 'compare_imaging_filters', storage: createJSONStorage(() => localStorage) }
  )
)

export const useProfile = create<RoleData>()(
  persist(
    set => ({
      facility: null,
      resetProfile: () => set({ facility: null, role: [], user: null }),
      role: [],
      setFacility: facility => set({ facility }),
      setRole: roles => set({ role: roles }),
      setUser: info => set({ user: info }),
      user: null
    }),
    { name: 'user-profile', storage: createJSONStorage(() => localStorage) }
  )
)

export type VitalPayload = { id: string } & Vitals

type VitalMock = {
  vitals: VitalPayload[]
  addVitals: (arg: VitalPayload) => void
  resetVitals: () => void
  editVitals: (arg: VitalPayload, id: string) => void
  deleteVitals: (id: string) => void
}

export const useVital = create<VitalMock>(set => ({
  addVitals: payload => set(state => ({ vitals: [...state.vitals, payload] })),
  deleteVitals: (id: string) => set(state => ({ vitals: state.vitals.filter(vital => vital?.id !== id) })),
  editVitals: (payload, id) =>
    set(state => {
      const index = state.vitals.findIndex(vital => vital?.id === id)
      if (index !== -1) {
        state.vitals[index] = payload
      }
      return state
    }),
  resetVitals: () => set({ vitals: [] }),
  vitals: []
}))

export const useMedicationStore = create<MedicationStore>(set => ({
  addMedication: medication =>
    set(state => ({
      medications: [
        ...state.medications,
        {
          ...medication,
          id: crypto.randomUUID(),
          timestamp: Date.now()
        }
      ]
    })),
  clearMedications: () => set({ medications: [] }),
  medications: [],
  removeMedication: id =>
    set(state => ({
      medications: state.medications.filter(med => med?.id !== id)
    }))
}))

export const useSidebarStore = create<{
  collapsed: boolean
  toggleSidebar: () => void
  expand: () => void
  collapse: () => void
}>(set => ({
  collapse: () => set({ collapsed: true }),
  collapsed: false,
  expand: () => set({ collapsed: false }),
  toggleSidebar: () => set(state => ({ collapsed: !state.collapsed }))
}))

interface HistoryStepStore {
  activeHistoryTab: string
  setActiveHistoryTab: (tab: string) => void
}

export const useHistoryStepStore = create<HistoryStepStore>(set => ({
  activeHistoryTab: 'Medical',
  setActiveHistoryTab: (tab: string) => set({ activeHistoryTab: tab })
}))

// Store to manage API strengths entries and concatenated generic name
export interface ApiStrengthEntry {
  id: string
  api_name: string
  strength: string
}

interface ProductApiStore {
  entries: ApiStrengthEntry[]
  // add new empty entry
  addEntry: (entry?: Partial<ApiStrengthEntry>) => void
  // update entry at index
  updateEntry: (index: number, entry: Partial<ApiStrengthEntry>) => void
  // remove entry at index
  removeEntry: (index: number) => void
  // set all entries
  setEntries: (entries: ApiStrengthEntry[]) => void
  // get concatenated string
  getConcatenated: () => string
}

export const useProductApiStore = create<ProductApiStore>(set => ({
  addEntry: (entry = {}) =>
    set(state => ({
      entries: [
        ...state.entries,
        {
          api_name: entry.api_name || '',
          id: crypto.randomUUID(),
          strength: entry.strength || ''
        }
      ]
    })),
  entries: [],
  getConcatenated: () => {
    // Note: this derived value is not reactive; call this function from components when needed
    // We can't access state directly here, so return empty - callers should compute from store entries
    return ''
  },
  removeEntry: index => set(state => ({ entries: state.entries.filter((_, i) => i !== index) })),
  setEntries: entries => set({ entries }),
  updateEntry: (index, entry) =>
    set(state => {
      const copy = [...state.entries]
      if (!copy[index]) return { entries: copy }
      copy[index] = { ...copy[index], ...entry }
      return { entries: copy }
    })
}))

export const useImagePreviewStore = create<ImagePreviewState>(set => ({
  imgFile: null,
  setImgFile: (file: File | null) => set({ imgFile: file })
}))

export { useEncounterLockStore } from './encounterLockStore'
